import type MainProject from '@/model/MainProject'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { serializeVirtualResources } from '@/features/virtual-resource/persistence'
import { writeMallNewBin } from '@/features/project-mall/runtime'
import {
  PROJECT_CURRENT_INFO,
  PROJECT_CURRENT_MALL_NEW,
  PROJECT_CURRENT_PROJECT_BIN,
  PROJECT_CURRENT_RES_JSON,
} from '@/features/project-cloud/ossPaths'
import BrowserUtil from '@/platform/oss/browserUtil'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import { RequestUtils } from '@/utils/request/requestUtil'
import { API_ENDPOINTS } from '@/config/apiEndpoints'
import {
  trimSlash,
  withQuery,
  type JsonRecord,
} from '@/utils/request/apiRequest'
import { APP_NAME, APP_VERSION } from '@/config/appVersion'

const webUrl = new BaseWebUrl()
const cgEndpoints = API_ENDPOINTS.cg
const cgApiEndpoints = API_ENDPOINTS.cgApi

export type CloudProjectOpenResponse = {
  projectName?: string
  projectsBinBase64: string
  virtualResourcesJson?: string | null
  mallNewBinBase64?: string | null
}

export type CloudProjectFetchResult =
  | { skipped: true }
  | {
      skipped: false
      projectName: string | null
      binBytes: Uint8Array
      virtualResourcesJson: string | null
      mallNewBytes: Uint8Array | null
      publishGindex: string | null
    }

export type NewProjectRemoteMeta = {
  guid: string
  createTime: string
}

/** 将云端接口返回的 base64 主包转回浏览器可解析的二进制。 */
function base64ToUint8Array(base64: string): Uint8Array {
  const normalized = base64.replace(/\s+/g, '')
  const binary = atob(normalized)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i)
  }
  return out
}

/** 归一化接口可选字符串，空字符串按无值处理。 */
function normalizeOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** 解码接口可选 base64 字段，字段缺失时返回空。 */
function decodeOptionalBase64(value: unknown): Uint8Array | null {
  const normalized = normalizeOptionalString(value)
  return normalized ? base64ToUint8Array(normalized) : null
}

/** 从 `projectInfo.txt` 中提取工程名，便于启动页展示。 */
function parseProjectNameFromInfo(text: string): string | null {
  const match = text.match(/(?:^|\n)\s*name\s*:\s*["']?([^"',\n}]+)["']?/i)
  const name = match?.[1]?.trim()
  return name || null
}

/** 从 `projectInfo.txt` 中提取已发布作品 gindex，恢复发布面板上下文。 */
function parseProjectGindexFromInfo(text: string): string | null {
  const match = text.match(/(?:^|\n)\s*gindex\s*:\s*["']?([^"',\n}]+)["']?/i)
  const gindex = match?.[1]?.trim()
  return gindex || null
}

/** 从多个候选字段中挑选第一个非空文本。 */
function normalizeText(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return ''
}

/** 将未知异常归一化为可展示的错误文本。 */
function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || '未知错误')
}

/** 兼容后端不同字段名，归一化新建工程接口响应。 */
function normalizeProjectCreateResponse(data: JsonRecord): NewProjectRemoteMeta {
  const payload = (data.data as JsonRecord | undefined) ?? data
  const guid = normalizeText(payload.guid, payload.project_guid, data.guid)
  const createTime = normalizeText(
    payload.create_time,
    payload.createTime,
    payload.time,
    data.create_time,
  )
  if (!guid || guid === 'null_project') {
    throw new Error(String(data.msg ?? payload.msg ?? '服务端未返回有效 guid'))
  }
  return { guid, createTime }
}

/** 生成新建工程埋点使用的设备哈希。 */
function getDeviceHash(): string {
  return RequestUtils.getMd5FromString(RequestUtils.osVersion || 'web')
}

/** 统计新建工程默认剧情事件数量，用于埋点。 */
function getNewProjectEventCount(project: MainProject | null): number {
  if (!project) return 0
  return project.storyTree?.Maps?.reduce((sum, map) => sum + (map.List?.length ?? 0), 0) ?? 0
}

/** 统计新建工程初始字数，用于埋点。 */
function getNewProjectWordCount(project: MainProject | null): number {
  const name = project?.storyTree?.ProjectName?.trim() || ''
  return name.length
}

/** 通过业务 HTTP 接口获取云端工程主包。 */
async function fetchCloudProjectFromHttp(url: string, projectKey: string): Promise<CloudProjectFetchResult> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify({ projectKey }),
    credentials: 'include',
  })
  if (!res.ok) {
    throw new Error(`云端读取工程失败：HTTP ${res.status}`)
  }

  const data = (await res.json()) as Partial<CloudProjectOpenResponse>
  const binBytes = decodeOptionalBase64(data.projectsBinBase64)
  if (!binBytes?.byteLength) {
    throw new Error('云端读取工程失败：工程主包为空')
  }
  const mallNewBytes = decodeOptionalBase64(data.mallNewBinBase64)

  return {
    skipped: false,
    projectName: normalizeOptionalString(data.projectName),
    binBytes,
    virtualResourcesJson: normalizeOptionalString(data.virtualResourcesJson),
    mallNewBytes,
    publishGindex: null,
  }
}

/** 从 OSS 工程信息文件读取工程名和已发布作品 gindex。 */
async function fetchCloudProjectInfoFromOss(projectKey: string): Promise<{
  projectName: string | null
  publishGindex: string | null
}> {
  const oss = BrowserUtil.getInstance()
  for (const projectInfoPath of [`${projectKey}/${PROJECT_CURRENT_INFO}`, `${projectKey}/projectInfo.txt`]) {
    try {
      if (await oss.hasObject(projectInfoPath)) {
        const projectInfoText = await oss.getText(projectInfoPath)
        return {
          projectName: parseProjectNameFromInfo(projectInfoText),
          publishGindex: parseProjectGindexFromInfo(projectInfoText),
        }
      }
    } catch {
      /* try next */
    }
  }
  return { projectName: null, publishGindex: null }
}

/** 从 OSS 候选路径读取新版商城数据。 */
async function fetchCloudMallNewFromOss(projectKey: string): Promise<Uint8Array | null> {
  const candidates = [
    `${projectKey}/${PROJECT_CURRENT_MALL_NEW}`,
    `${projectKey}/MallNew.bin`,
  ]
  for (const path of candidates) {
    try {
      return await BrowserUtil.getInstance().getUint8Array(path)
    } catch {
      /* try next */
    }
  }
  return null
}

/** 直接从 OSS 读取工程主包和虚拟资源映射。 */
async function fetchCloudProjectFromOss(projectKey: string): Promise<CloudProjectFetchResult> {
  const oss = BrowserUtil.getInstance()
  const binCandidates = [
    `${projectKey}/${PROJECT_CURRENT_PROJECT_BIN}`,
    `${projectKey}/projectS.bin`,
    `${projectKey}/projects.bin`,
  ]
  let binBytes: Uint8Array | null = null
  let lastError = ''
  for (const path of binCandidates) {
    try {
      const currentBytes = await oss.getUint8Array(path)
      if (currentBytes.byteLength > 0) {
        binBytes = currentBytes
        break
      }
    } catch (error) {
      lastError = `${path} · ${getErrorMessage(error)}`
      /* try next */
    }
  }
  if (!binBytes?.byteLength) {
    throw new Error(`OSS 中未找到工程主包 projectS.bin${lastError ? `；最后错误：${lastError}` : ''}`)
  }

  let virtualResourcesJson: string | null = null
  for (const path of [`${projectKey}/${PROJECT_CURRENT_RES_JSON}`, `${projectKey}/res.json`]) {
    try {
      virtualResourcesJson = await oss.getText(path)
      break
    } catch {
      virtualResourcesJson = null
    }
  }

  const projectInfo = await fetchCloudProjectInfoFromOss(projectKey)

  return {
    skipped: false,
    projectName: projectInfo.projectName,
    binBytes,
    virtualResourcesJson,
    mallNewBytes: await fetchCloudMallNewFromOss(projectKey),
    publishGindex: projectInfo.publishGindex,
  }
}

/** HTTP 打开成功后，从 OSS 补齐商城数据、工程名和发布上下文。 */
async function fillCloudSidecarsFromOssIfNeeded(
  result: CloudProjectFetchResult,
  projectKey: string,
): Promise<CloudProjectFetchResult> {
  if (result.skipped) {
    return result
  }
  if (!result.mallNewBytes) {
    result.mallNewBytes = await fetchCloudMallNewFromOss(projectKey)
  }
  const projectInfo = await fetchCloudProjectInfoFromOss(projectKey)
  result.projectName = result.projectName || projectInfo.projectName
  result.publishGindex = result.publishGindex || projectInfo.publishGindex
  return result
}

/** 初始化云端打开所需 OSS 配置，失败时返回错误文本而不是直接中断。 */
function tryInitOssForCloudOpen(): { configured: boolean; error: string } {
  try {
    return {
      configured: BrowserUtil.getInstance().initDefaultConfig().isConfigured(),
      error: '',
    }
  } catch (error) {
    return {
      configured: false,
      error: getErrorMessage(error),
    }
  }
}

/** 将二进制工程数据编码为 base64，避免大文件直接展开导致调用栈溢出。 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize)
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

/** 从云端获取工程主包与虚拟资源映射。 */
export async function fetchCloudProjectIfConfigured(
  projectKey: string,
): Promise<CloudProjectFetchResult> {
  const url = normalizeOptionalString(import.meta.env.VITE_CLOUD_OPEN_PROJECT_URL)
  let httpError = ''
  let ossInitError = ''
  if (url) {
    try {
      const result = await fetchCloudProjectFromHttp(url, projectKey)
      const ossState = tryInitOssForCloudOpen()
      ossInitError = ossState.error
      if (!ossState.configured) return result
      try {
        return await fillCloudSidecarsFromOssIfNeeded(result, projectKey)
      } catch {
        return result
      }
    } catch (error) {
      httpError = getErrorMessage(error)
    }
  }
  const ossState = tryInitOssForCloudOpen()
  ossInitError = ossState.error
  if (!ossState.configured) {
    if (httpError) {
      throw new Error(`云端数据请求失败：HTTP 打开接口失败：${httpError}；OSS 初始化失败：${ossInitError || '不可用'}`)
    }
    return { skipped: true }
  }
  try {
    return await fetchCloudProjectFromOss(projectKey)
  } catch (error) {
    const ossError = getErrorMessage(error)
    throw new Error(httpError
      ? `云端数据请求失败：HTTP 打开接口失败：${httpError}；OSS 回退失败：${ossError}`
      : `云端数据请求失败：OSS 打开失败：${ossError}`)
  }
}

/** 请求服务端创建云端工程元信息（`guid` / `create_time`）。 */
export async function requestNewProjectRemoteMeta(params: {
  sessionId: string
  projName: string
  channelId?: number
  toolVersion?: string
}): Promise<NewProjectRemoteMeta> {
  const createRes = await fetch(
    `${trimSlash(webUrl.baseCg)}/api${withQuery(cgApiEndpoints.createProject, {
      sessionid: params.sessionId,
      proj_name: params.projName.trim(),
      channel_id: params.channelId ?? 0,
      tool_version: params.toolVersion ?? APP_VERSION,
      tool_name: APP_NAME,
    })}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
  if (!createRes.ok) {
    throw new Error(`新建工程远端初始化失败：HTTP ${createRes.status}`)
  }

  const createJson = (await createRes.json()) as JsonRecord
  return normalizeProjectCreateResponse(createJson)
}

/** 上报“新建工程”埋点，不阻断主流程。 */
export async function recordCreateTracking(params: {
  sessionId: string
  uid: number
  guid: string
  project?: MainProject | null
}): Promise<boolean> {
  const query = RequestUtils.getSortString(
    [
      `sessionid=${encodeURIComponent(params.sessionId)}`,
      'operate_id=2',
      `guid=${encodeURIComponent(params.guid)}`,
      `uid=${params.uid}`,
      `mac_md5=${getDeviceHash()}`,
      `tool_version=${encodeURIComponent(RequestUtils.getToolVersion())}`,
      `event_num=${getNewProjectEventCount(params.project ?? null)}`,
      `word_num=${getNewProjectWordCount(params.project ?? null)}`,
    ].join('&'),
  )

  const url = `${trimSlash(webUrl.baseCg)}${cgEndpoints.recordCreateTracking}?${query}`
  try {
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * 可选：将最新工程主包同步到业务云端接口，避免“云端打开”优先走 HTTP 时读到旧缓存。
 * 未配置 `VITE_CLOUD_SAVE_PROJECT_URL` 时直接跳过，不影响现有 OSS 保存。
 */
export async function syncCloudProjectIfConfigured(params: {
  project: MainProject
  virtualResourceRoots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
}): Promise<{ ok: boolean; skipped: boolean }> {
  const url = normalizeOptionalString(import.meta.env.VITE_CLOUD_SAVE_PROJECT_URL)
  if (!url) return { ok: true, skipped: true }

  const projectKey = params.project.projectHeader.ID?.trim()
  if (!projectKey) {
    throw new Error('工程缺少 guid，无法同步到云端接口')
  }

  const projectsBinBase64 = uint8ArrayToBase64(params.project.writeBinBuffer())
  const mallNewBinBase64 = uint8ArrayToBase64(writeMallNewBin(params.project))
  const virtualResourcesJson = serializeVirtualResources(params.virtualResourceRoots)
  const projectName = params.project.storyTree?.ProjectName?.trim() || ''

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    credentials: 'include',
    body: JSON.stringify({
      projectKey,
      projectName,
      projectsBinBase64,
      mallNewBinBase64,
      virtualResourcesJson,
    }),
  })
  if (!res.ok) {
    throw new Error(`云端工程同步失败：HTTP ${res.status}`)
  }
  return { ok: true, skipped: false }
}
